import { z } from 'zod';
import { paginationQuerySchema } from '../common/pagination.js';
import { isUzPhone, toUzPhone } from '../common/phone.js';
/**
 * OMBOR XO'JALIGI — PIN xodimlar, ulangan telefonlar va javondan ishlatish.
 *
 * Ikki yuza bor va ular ATAYLAB ajratilgan:
 *   - web (`/subdivisions/mine/...`) — mas'ul xodim boshqaradi, tarixni ko'radi;
 *   - mobil (`/mobile/...`) — omborda turgan telefon: xodim ismini tanlaydi,
 *     PIN teradi, etiketkani skanerlaydi va nechta olganini yozadi.
 */
const numericId = z.string().regex(/^\d+$/);
/**
 * XODIMNING TELEFON RAQAMI — mobil ilovada uning NOMI o'rnida turadi.
 *
 * Kelgan matn qanday yozilganidan qat'i nazar BITTA shaklga keltiriladi:
 * `+998901234567`. Aks holda bir xil raqam bazada `+998 90 123-45-67`,
 * `998901234567` va `901234567` bo'lib uch marta yotib, bitta odam uch
 * xodimga aylanardi. Solishtirish baribir oxirgi 9 raqam bo'yicha ketadi
 * (indeks ham, Telegram orqali kirish ham) — lekin ro'yxatga bir xil
 * ko'rinishda tushgani ma'qul.
 *
 * Tekshiruv QAT'IY: to'liq 9 xona. Ilgari "kamida 9 ta raqam" edi va u
 * `+998 90 123-45-67 89` kabi uzun yozuvni ham o'tkazib yuborardi.
 */
export const workerPhoneSchema = z
    .string()
    .trim()
    .min(7)
    .max(20)
    .refine(isUzPhone, { message: 'Telefon raqami to\'liq emas' })
    .transform(toUzPhone);
/* ------------------------------ Xodimlar ------------------------------ */
export const storeroomWorkerSchema = z.object({
    id: z.string(),
    fullName: z.string(),
    position: z.string().nullable(),
    /** Mobil ilovaga shu raqam bilan kiradi. */
    phone: z.string().nullable(),
    /** Kadrlar ro'yxatidagi xodim bo'lsa — uning `user.id` si. */
    userId: z.string().nullable(),
    storeroomIds: z.array(z.string()),
    isActive: z.boolean(),
    /** 5 marta xato PIN dan keyin shu vaqtgacha kira olmaydi. */
    lockedUntil: z.string().nullable(),
    lastUsedAt: z.string().nullable(),
    /**
     * O'CHIRISH mumkinmi. Server hisoblaydi, chunki qoida bitta joyda
     * turishi kerak: xodimni faqat uni QO'SHGAN odam va faqat u hali
     * hech narsa olmagan bo'lsa o'chira oladi. Mahsulot olingan bo'lsa
     * jurnal o'zgarmas qolishi kerak — unda o'chirish emas,
     * faolsizlantirish ishlatiladi.
     */
    canDelete: z.boolean(),
});
export const createStoreroomWorkerSchema = z
    .object({
    userId: numericId.optional(),
    fullName: z.string().trim().min(3).max(200).optional(),
    position: z.string().trim().max(120).nullish(),
    /* MAJBURIY: raqamsiz xodim mobil ilovaga umuman kira olmaydi, ya'ni PIN
       ham, ombor ruxsati ham foydasiz bo'lib qolardi. */
    phone: workerPhoneSchema,
    storeroomIds: z.array(numericId).min(1).max(50),
})
    .refine((v) => v.userId !== undefined || v.fullName !== undefined, {
    message: 'Xodimni ro\'yxatdan tanlang yoki F.I.O. ni yozing',
    path: ['fullName'],
});
export const updateStoreroomWorkerSchema = z
    .object({
    fullName: z.string().trim().min(3).max(200).optional(),
    position: z.string().trim().max(120).nullable().optional(),
    phone: workerPhoneSchema.optional(),
    storeroomIds: z.array(numericId).min(1).max(50).optional(),
    isActive: z.boolean().optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: "O'zgartirish yo'q" });
/** PIN FAQAT shu javobda, bir marta ko'rinadi — bazada faqat xeshi turadi. */
export const workerPinResultSchema = z.object({
    worker: storeroomWorkerSchema,
    pin: z.string().regex(/^\d{6}$/),
});
/** Kadrlar ro'yxatidan qidiruv — faqat ism va lavozim, shaxsiy ma'lumotsiz. */
export const colleagueSchema = z.object({
    userId: z.string(),
    fullName: z.string(),
    positionName: z.string().nullable(),
});
/* ----------------------------- Qurilmalar ----------------------------- */
/*
 * QURILMA TUSHUNCHASI OLIB TASHLANDI.
 *
 * Ilgari telefon OMBORGA ulanardi (bir martalik QR) va ruxsat o'sha
 * ulanishdan kelardi. Amalda esa har xodimning O'Z telefoni bor, ya'ni
 * umumiy telefonni ulash ortiqcha qadam edi. Endi xodim o'z raqami va PIN
 * bilan kiradi, ruxsat esa `storeroom_worker_access` dan — ya'ni begona
 * ombor lotini skanerlagan odam hech narsa ko'rmaydi.
 *
 * `storeroom_device` JADVALI bazada qoladi: eski `stock_usage` yozuvlari
 * unga tayanadi va ishlatish jurnali o'zgarmas hujjat. Yangi qurilma
 * yaratilmaydi, shuning uchun bu yerda sxema ham qolmadi.
 */
/* ------------------------------ Ishlatish ----------------------------- */
export const stockUsageRowSchema = z.object({
    id: z.string(),
    usedAt: z.string(),
    qty: z.number(),
    unit: z.string(),
    comment: z.string(),
    workerId: z.string(),
    workerName: z.string(),
    deviceName: z.string().nullable(),
    lotId: z.string(),
    code: z.string(),
    nomenclatureName: z.string(),
    storeroomName: z.string(),
    zoneName: z.string().nullable(),
    requisitionId: z.string().nullable(),
    requisitionDocNo: z.string().nullable(),
});
export const stockUsageQuerySchema = paginationQuerySchema.extend({
    storeroomId: numericId.optional(),
    workerId: numericId.optional(),
    lotId: numericId.optional(),
});
/* -------------------------------- Mobil -------------------------------- */
/**
 * KIRISH — telefon raqami va PIN.
 *
 * Xodim ro'yxatdan tanlanmaydi va bu ataylab: ilova APK bo'lib tarqaladi,
 * ya'ni uni o'rnatgan HAR KIM ochishi mumkin. Ism ro'yxatini ko'rsatish
 * begona odamga butun bo'linmaning xodimlar ro'yxatini berib qo'yardi.
 * Raqamni esa faqat egasi biladi.
 */
export const mobileLoginSchema = z.object({
    phone: workerPhoneSchema,
    pin: z.string().regex(/^\d{6}$/, 'PIN 6 ta raqam'),
});
/** Xodimga biriktirilgan ombor — skanerlash aynan shular ichida ishlaydi. */
export const mobileStoreroomSchema = z.object({
    id: z.string(),
    name: z.string(),
    subdivisionName: z.string(),
});
export const mobileWorkerSchema = z.object({
    id: z.string(),
    fullName: z.string(),
    position: z.string().nullable(),
});
export const mobileSessionSchema = z.object({
    token: z.string(),
    expiresAt: z.string(),
    worker: mobileWorkerSchema,
    /* Ilova ularni sarlavhada ko'rsatadi: xodim qaysi omborlardan ola
       olishini kirgan zahoti bilib tursin, skanerlab xato olgandan keyin
       emas. */
    storerooms: z.array(mobileStoreroomSchema),
});
export const mobileLotSchema = z.object({
    lotId: z.string(),
    code: z.string(),
    nomenclatureName: z.string(),
    unit: z.string(),
    shelfQty: z.number(),
    storeroomName: z.string(),
    zoneName: z.string().nullable(),
    batchNo: z.string(),
    requisitionDocNo: z.string().nullable(),
});
export const mobileUseInputSchema = z.object({
    qty: z.number().positive().max(1_000_000),
    comment: z.string().trim().min(2, 'Izoh yozing').max(500),
});
export const mobileUseResultSchema = z.object({
    usageId: z.string(),
    lot: mobileLotSchema,
});
export const mobileUsageRowSchema = z.object({
    id: z.string(),
    usedAt: z.string(),
    qty: z.number(),
    unit: z.string(),
    comment: z.string(),
    nomenclatureName: z.string(),
    code: z.string(),
});
//# sourceMappingURL=storekeeping.schema.js.map